import type { ScoreEntry, ScoreboardView, StarKind } from '@dreamquiz/shared';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const GOLD = '#fbbf24';
const DIM = '#5b4b7a';
/** Konfetti-Farben (Wunsch Niggo 2026-09-13) - Gold + die übrigen Theme-Akzente. */
const CONFETTI_COLORS = ['#d4af37', '#a78bfa', '#4ade80', '#fbbf24', '#f87171', '#60a5fa'];
const CROWN_PULSE_MS = 2400;

let starId = 0;

/** Stern als SVG - voll / halb / leer (Konzept Kap. 7, Rundenende). */
function starSvg(kind: StarKind, size: number, delayMs = 0): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'star');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  if (delayMs && !REDUCED_MOTION) svg.style.animationDelay = `${delayMs}ms`;

  let fill = kind === 'full' ? GOLD : DIM;

  if (kind === 'half') {
    const id = `star-grad-${++starId}`;
    const defs = document.createElementNS(ns, 'defs');
    const grad = document.createElementNS(ns, 'linearGradient');
    grad.setAttribute('id', id);
    for (const [offset, color] of [
      ['50%', GOLD],
      ['50%', DIM],
    ] as const) {
      const stop = document.createElementNS(ns, 'stop');
      stop.setAttribute('offset', offset);
      stop.setAttribute('stop-color', color);
      grad.appendChild(stop);
    }
    defs.appendChild(grad);
    svg.appendChild(defs);
    fill = `url(#${id})`;
  }

  const poly = document.createElementNS(ns, 'polygon');
  poly.setAttribute(
    'points',
    '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26',
  );
  poly.setAttribute('fill', fill);
  svg.appendChild(poly);
  return svg;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Das Scoreboard rechts. Reine Anzeige, keine Eingabe (Konzept Kap. 7).
 * Alle Texte kommen als textContent hinein - siehe Hinweis in terminal.ts.
 */
export class Scoreboard {
  private counters: number[] = [];
  /** Rundenende-Effekt (Gold-Puls + Konfetti, Wunsch Niggo 2026-09-13) nur EINMAL pro Rundenende auslösen, nicht bei jedem erneuten Render desselben Endstands. */
  private crownEffectFired = false;

  constructor(private readonly root: HTMLElement) {}

  render(view: ScoreboardView): void {
    for (const id of this.counters) window.clearInterval(id);
    this.counters = [];

    this.root.replaceChildren();

    const head = el('div', 'sbh');
    head.appendChild(el('span', undefined, view.title ?? 'scoreboard'));
    head.appendChild(el('span', undefined, view.subtitle ?? ''));
    this.root.appendChild(head);

    if (!view.players.length) {
      this.root.appendChild(el('div', 'idle', view.hint ?? 'Noch kein Spiel.'));
      this.crownEffectFired = false;
      return;
    }

    const isEndView = view.title === '🏁 Rundenende';
    const fireCrownEffect = isEndView && !this.crownEffectFired;
    view.players.forEach((player, index) => this.renderPlayer(player, index, fireCrownEffect));
    this.crownEffectFired = isEndView;

    if (view.legend) this.root.appendChild(this.renderLegend());
  }

  private renderPlayer(player: ScoreEntry, index: number, fireCrownEffect: boolean): void {
    const card = el('div', `p${player.me ? ' me' : ''}`);

    const nameRow = el('div', 'nm');
    const left = el('span');
    const badges = `${player.crown ? '👑 ' : ''}${player.isAdmin ? '🛡️ ' : ''}${player.roomOwner ? '🔑 ' : ''}`;
    left.textContent = `${badges}${player.name}`;
    if (player.me) {
      const you = el('span', 's-muted', ' (du)');
      you.style.color = 'var(--mu)';
      left.appendChild(you);
    }
    nameRow.appendChild(left);

    const right = el('span');
    right.style.color = 'var(--mu)';
    if (player.status === 'answered') {
      right.textContent = '✓';
      right.style.color = 'var(--ok)';
    } else if (player.status === 'pending') {
      right.textContent = '…';
    } else if (player.status === 'offline') {
      right.textContent = '⚠';
      right.style.color = 'var(--wa)';
    } else {
      right.textContent = `#${index + 1}`;
    }
    nameRow.appendChild(right);
    card.appendChild(nameRow);

    if (player.stars?.length) {
      const stars = el('div', 'stars');
      player.stars.forEach((star, j) => {
        const cell = el('span', 'sc');
        cell.title = star.label;
        cell.appendChild(starSvg(star.kind, 24, index * 350 + j * 180));
        cell.appendChild(el('small', undefined, star.icon));
        stars.appendChild(cell);
      });
      card.appendChild(stars);
    }

    const gain = player.gain ?? 0;
    const points = el('div', 'pts');
    const value = el('b', undefined, String(player.score - gain));
    points.appendChild(value);
    points.appendChild(document.createTextNode(' Pkt'));
    card.appendChild(points);

    this.root.appendChild(card);

    if (gain > 0) this.animateGain(card, value, player, gain);

    // Rundenende-Feier für den/die Gewinner (Wunsch Niggo 2026-09-13): Rahmen
    // wird/bleibt gold, dazu 3x "Atmen" (Puls) plus Konfetti aus den unteren
    // Kartenecken - einmalig pro Rundenende, nicht bei jedem Re-Render.
    if (player.crown) {
      card.classList.add('crown');
      if (fireCrownEffect && !REDUCED_MOTION) {
        card.classList.add('crown-pulse');
        window.setTimeout(() => card.classList.remove('crown-pulse'), CROWN_PULSE_MS);
        this.spawnConfetti(card);
      }
    }
  }

  /** Konfetti aus der unteren linken und rechten Kartenecke, auf die Karte selbst begrenzt (Wunsch Niggo 2026-09-13: "nur innerhalb seiner Karte"). */
  private spawnConfetti(card: HTMLElement): void {
    card.classList.add('confetti-host');
    const pieces: HTMLElement[] = [];
    for (const side of ['left', 'right'] as const) {
      const dir = side === 'left' ? 1 : -1;
      for (let i = 0; i < 14; i++) {
        const piece = el('span', 'confetti-piece');
        piece.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]!;
        piece.style[side] = '0px';
        const dx = dir * (24 + Math.random() * 70);
        piece.style.setProperty('--cdx', `${dx}px`);
        piece.style.setProperty('--cpeak', `${-(45 + Math.random() * 55)}px`);
        piece.style.setProperty('--cend', `${-(8 + Math.random() * 24)}px`);
        piece.style.setProperty('--crot1', `${Math.round(Math.random() * 360 - 180)}deg`);
        piece.style.setProperty('--crot2', `${Math.round(Math.random() * 720 - 360)}deg`);
        piece.style.animationDelay = `${Math.round(Math.random() * 160)}ms`;
        card.appendChild(piece);
        pieces.push(piece);
      }
    }
    window.setTimeout(() => {
      for (const piece of pieces) piece.remove();
      card.classList.remove('confetti-host');
    }, 1500);
  }

  /** Animierte "+10" / "+12 ⚡"-Anzeige und Hochzählen (Konzept Kap. 7). */
  private animateGain(
    card: HTMLElement,
    value: HTMLElement,
    player: ScoreEntry,
    gain: number,
  ): void {
    const from = player.score - gain;

    const pop = el('span', `pop${player.bonus ? ' sp' : ''}`);
    pop.textContent = `+${gain}${player.bonus ? ' ⚡' : ''}`;
    card.appendChild(pop);
    card.classList.add('flash');
    window.setTimeout(() => card.classList.remove('flash'), 800);

    if (REDUCED_MOTION) {
      value.textContent = String(player.score);
      return;
    }

    const steps = 12;
    let step = 0;
    const timer = window.setInterval(() => {
      step++;
      value.textContent = String(Math.round(from + (gain * step) / steps));
      if (step >= steps) window.clearInterval(timer);
    }, 40);
    this.counters.push(timer);
  }

  private renderLegend(): HTMLElement {
    const legend = el('div', 'leg');
    const rows: [StarKind, string][] = [
      ['full', 'alle Fragen der Kategorie richtig'],
      ['half', 'teilweise richtig'],
      ['empty', 'alle falsch'],
    ];
    for (const [kind, label] of rows) {
      const row = el('span');
      row.appendChild(starSvg(kind, 14));
      row.appendChild(document.createTextNode(label));
      legend.appendChild(row);
    }
    return legend;
  }
}
